import { useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useLocation } from 'react-router-dom'

import { EnumTerritoriesFiltersType, ListTerrotoriesSortOrder, TimeUnit } from 'generated/graphql'
import useGetUnifiedTerritories from 'modules/territory/common/hooks/use-get-unified-territories'
import { analyticsTracker } from 'utils'

import type { TGlobalTopicListTab } from './global-topic-list-tab.types'
import { DEFAULT_SORT, DEFAULT_TIME, PAGINATION_LIMIT } from '../common/global-topic.consts'
import type { TTableOrder } from '../common/global-topic.types'
import { handleSort } from '../common/global-topic.utils'

export const useGlobalTopicListTabModel = (): TGlobalTopicListTab => {
  const [order, setOrder] = useState<TTableOrder>(DEFAULT_SORT)
  const location = useLocation()

  const variables = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const dateRangeValue = searchParams.get('dateRange')
    const timeRange = {
      timeUnit: TimeUnit.Month,
      timeValue: Number(dateRangeValue) || DEFAULT_TIME,
    }

    return {
      pagination: {
        take: PAGINATION_LIMIT,
      },
      filters: {
        types: [EnumTerritoriesFiltersType.GlobalTopic] as EnumTerritoriesFiltersType[],
        timeRange,
      },
      timeRange,
      sort: order,
      audience: {},
    }
  }, [location.search, order])

  const { data, error, fetchMore, isLoading, refetch, hasMore, totalCount, isFetchingMore } = useGetUnifiedTerritories({
    variables,
    audience: {},
  })

  const handleSetOrder = field => {
    setOrder(prevState => {
      const previousOrder = prevState.order === 'desc' ? ListTerrotoriesSortOrder.Asc : ListTerrotoriesSortOrder.Desc
      const newOrder = {
        field,
        order: prevState.field === field ? previousOrder : ListTerrotoriesSortOrder.Desc,
      }

      const { SORT } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(SORT, {
        Page: 'Search Topics Page',
        'Sort field': field,
        'Sort order': newOrder.order,
      })

      return newOrder
    })
  }

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || isFetchingMore,
    hasNextPage: hasMore,
    onLoadMore: fetchMore,
    disabled: !!error,
  })

  const sortedData = useMemo(() => {
    return handleSort(data, order)
  }, [data, order])

  return {
    data: sortedData,
    error: Boolean(error),
    isLoading,
    refetch,
    hasMore,
    totalCount,
    isFetchingMore,
    handleSetOrder,
    order,
    sentryRef,
  }
}
